/**
 * src/utils/commBadgeEvents.js
 *
 * Producer / consumer helpers for the `comm_badge_events` BroadcastChannel that drives the
 * Chat / Channels unread badges between server polls.
 *
 * Why this exists: every open tab has its own workspace socket, so every tab receives the
 * same `message_created` event and each one posts a "+1" to the channel — and a
 * BroadcastChannel post reaches every listener in every tab, including the sender's own
 * provider. With K tabs one message incremented every badge by ~K.
 *
 * Fix: each increment carries the identity of the server event (the message id). Consumers
 * apply an increment once per event id, no matter how many tabs relay it, so one logical
 * message produces exactly one unread-count change. The 45-second server poll remains the
 * authoritative reconciliation and simply overwrites the optimistic count.
 */

export const COMM_BADGE_CHANNEL = "comm_badge_events";

/** Best-effort stable id for a message_created payload; null when none is available. */
export function messageEventId(message, scopeId) {
  if (!message || typeof message !== "object") return null;
  const id = message.message_id || message.id || message._id;
  if (id) return String(id);
  if (message.created_at && (scopeId || message.chat_id || message.channel_id)) {
    return `${scopeId || message.chat_id || message.channel_id}:${message.sender_id || ""}:${message.created_at}`;
  }
  return null;
}

/**
 * Post a badge event to every tab (including this one).
 * @param {"new_chat_message"|"new_channel_message"|"chat_read"|"channel_read"|"mail_read"|"refresh"} type
 * @param {object} [extra]  e.g. { eventId, delta }
 */
export function postCommBadgeEvent(type, extra = {}) {
  if (typeof window === "undefined" || typeof window.BroadcastChannel !== "function") return;
  try {
    const bc = new BroadcastChannel(COMM_BADGE_CHANNEL);
    bc.postMessage({ ...extra, type });
    bc.close();
  } catch {
    /* BroadcastChannel unsupported — the server poll keeps counts fresh */
  }
}

/**
 * Small bounded de-duplication set: `accept(id)` returns true the first time an id is seen
 * and false afterwards. Events without an id are always accepted (previous behaviour).
 */
export function createEventDeduper(limit = 500, ttlMs = 0) {
  const order = [];
  const seen = new Map(); // id -> first-seen time
  const isLive = (id, now) => seen.has(id) && (!ttlMs || now - seen.get(id) <= ttlMs);
  return {
    accept(id, now = Date.now()) {
      if (!id) return true;
      if (isLive(id, now)) return false;
      if (!seen.has(id)) order.push(id);
      seen.set(id, now);
      if (order.length > limit) seen.delete(order.shift());
      return true;
    },
    has(id, now = Date.now()) {
      return Boolean(id) && isLive(id, now);
    },
  };
}

/**
 * Identity of a real-time domain event (approval / revenue / rbac) for de-duplication.
 * Prefers explicit ids; otherwise the serialized payload, which is identical for every tab
 * because it is the same server broadcast.
 */
export function domainEventId(kind, data) {
  if (!data || typeof data !== "object") return null;
  // Only fields that identify the EVENT itself. `data.id` is deliberately not used: domain
  // payloads may carry the entity id there, and two different events about one entity would
  // then collapse into one.
  const explicit = data.event_id || data.message_id;
  if (explicit) return `${kind}:${explicit}`;
  try {
    return `${kind}:${JSON.stringify(data)}`;
  } catch {
    return null;
  }
}
